import { DsIcons, type DsIcon } from '@/ds/icons'
import type { FeatureState } from '@/features/flags/useFeature'
import { payRentStrings as s } from '@/features/payments/pay-rent-strings'
import type { Lease, PaymentGroup, PaymentGroupKey, Rent } from '@/types/models'

/** Rent can be paid on an active lease, and on one whose cancellation is only requested (it still owes rent). Not ended, not payment-pending. */
export const isPayableLease = (l: Lease) => l.status === 'active' || l.status === 'cancellation-requested'

export const leaseDueAmount = (l: Lease | undefined) => (l ? Math.max((l.stat?.expected ?? 0) - (l.stat?.collected ?? 0), 0) : 0)

export function quickAmounts(lease: Lease | undefined): { label: string; amount: number }[] {
  if (!lease) return []
  const due = leaseDueAmount(lease)
  const out = [{ label: s.quickFullRent, amount: lease.rent }]
  if (due > 0 && due !== lease.rent) out.push({ label: s.quickDue, amount: due })
  return out
}

export function amountHelper(amount: number | null, due: number, format: (n: number) => string): string | undefined {
  if (due <= 0) return s.nothingDue
  if (amount && amount > due) return s.paysAhead(format(due))
  return undefined
}

export interface GroupAvailability {
  /** Shown at all (flag enabled or coming soon; offline always). */
  visible: boolean
  /** Selectable; otherwise tapping toasts `reason`. */
  enabled: boolean
  reason?: string
}

// Flags decide visibility, the landlord's banking status usability, their country whether online is offered at all.
// `paymentsAllowed` is separate from `flags`: a flag is a global rollout switch, a capability is per country.
// Recording offline is never gated: that money already changed hands outside the app.
export function groupAvailability(
  group: PaymentGroup,
  flags: { online: FeatureState; upi: FeatureState },
  banking: { hasVerifiedBanking: boolean; hasVerifiedUpi: boolean } | undefined,
  paymentsAllowed = true,
): GroupAvailability {
  if (group.isOffline) return { visible: true, enabled: true }
  // Checked before the flags: a country that does not do online collection hides every online
  // method outright, rather than showing one greyed out with a "coming soon" that will never come.
  if (!paymentsAllowed) return { visible: false, enabled: false }
  if (group.isUpiDirect) {
    if (!flags.upi.isVisible) return { visible: false, enabled: false }
    if (flags.upi.isComingSoon) return { visible: true, enabled: false, reason: s.upiComingSoon }
    if (banking && !banking.hasVerifiedUpi) return { visible: true, enabled: false, reason: s.upiUnavailable }
    return { visible: true, enabled: true }
  }
  if (!flags.online.isVisible) return { visible: false, enabled: false }
  if (flags.online.isComingSoon) return { visible: true, enabled: false, reason: s.onlineComingSoon }
  if (banking && !banking.hasVerifiedBanking) return { visible: true, enabled: false, reason: s.onlineUnavailable }
  return { visible: true, enabled: true }
}

export const isOnlineGroup = (key: PaymentGroupKey) => key === 'standard' || key === 'premium'

export function successShareText(title: string, amount: string, property: string, paidBy: string | undefined, paidOn: string): string {
  return [title, `Total: ${amount}`, `Property: ${property}`, ...(paidBy ? [`Paid by: ${paidBy}`] : []), `Paid on: ${paidOn}`].join('\n')
}

export interface OpenPeriod {
  rent: Rent
  balance: number
  /** Balance of this period and every older open one: the amount that clears up to here. */
  cumulative: number
}

// Unpaid rent periods, oldest first, with running totals. The backend applies a payment to the oldest open rent
// first, so a period can only be settled together with those before it. Cancelled rents and ones with a
// payment in flight are left out.
export function openPeriods(rents: Rent[]): OpenPeriod[] {
  let running = 0
  return [...rents]
    .filter((r) => r.status !== 'canceled' && r.status !== 'pending' && r.expectedAmount - r.paidAmount > 0)
    .sort((a, b) => Date.parse(a.rentDate) - Date.parse(b.rentDate))
    .map((rent) => {
      const balance = rent.expectedAmount - rent.paidAmount
      running += balance
      return { rent, balance, cumulative: running }
    })
}

export function periodCoverage(periods: OpenPeriod[], amount: number | null): ('full' | 'part' | 'none')[] {
  const a = amount ?? 0
  return periods.map((p) => (a >= p.cumulative ? 'full' : a > p.cumulative - p.balance ? 'part' : 'none'))
}

export function groupCard(g: PaymentGroup): { title: string; description: string; icon: DsIcon } {
  if (g.isOffline) return { title: s.methodCash, description: s.methodCashDescription, icon: DsIcons.wallet }
  if (g.isUpiDirect) return { title: g.label, description: s.methodUpiDescription, icon: DsIcons.mobile }
  return { title: g.label, description: s.methodOnlineDescription, icon: DsIcons.card }
}
